import { useState, useEffect, useCallback, useRef } from 'react'
import { authenticatedFetchJson } from '../services/api/apiClient'
import { apiEndpoints } from '../consts'
import logger from '../utils/logger'

/**
 * Hook for fetching billing customer data.
 *
 * When clientAccountId is provided, fetches and validates a single customer.
 * Without a filter, picks the first result (with a warning if multiple exist).
 *
 * NOTE: User-level scoping (so clients only see their own data) will be enforced
 * server-side when user-level auth is added. Until then, the /billing page should
 * only be used by admins or in contexts where the filter is provided.
 *
 * @param {string|number} [clientAccountId] - Optional filter by client_account_id
 */
const useBillingData = (clientAccountId, campaignName) => {
  const [customer, setCustomer] = useState(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const abortControllerRef = useRef(null)

  const fetchBillingData = useCallback(async (signal) => {
    try {
      setLoading(true)
      setError(null)
      let url = apiEndpoints.billingCustomers
      const params = new URLSearchParams()
      if (clientAccountId) params.set('client_account_id', clientAccountId)
      if (campaignName) params.set('campaign_name', campaignName)
      if (params.toString()) url += `?${params.toString()}`
      const response = await authenticatedFetchJson(url, { signal })
      const customers = response?.data || []

      if (clientAccountId) {
        // Filtered fetch — expect exactly 1 result
        if (customers.length === 1) {
          setCustomer(customers[0])
        } else if (customers.length === 0) {
          logger.warn('No billing customer found for clientAccountId', { clientAccountId })
          setCustomer(null)
          setError('No billing profile found for this account.')
        } else {
          logger.error('Multiple billing customers found for clientAccountId', { clientAccountId, count: customers.length })
          setCustomer(null)
          setError('Multiple billing profiles found for this account.')
        }
      } else {
        // Unfiltered — pick first, warn if multiple
        if (customers.length > 1) {
          logger.warn('Multiple billing customers returned without clientAccountId filter', { count: customers.length })
        }
        setCustomer(customers.length > 0 ? customers[0] : null)
      }
    } catch (err) {
      if (err.name === 'AbortError') return
      logger.error('Failed to fetch billing data:', err)
      setError(err.message || 'Failed to load billing information')
    } finally {
      setLoading(false)
    }
  }, [clientAccountId, campaignName])

  useEffect(() => {
    if (abortControllerRef.current) {
      abortControllerRef.current.abort()
    }
    const controller = new AbortController()
    abortControllerRef.current = controller
    fetchBillingData(controller.signal)
    return () => controller.abort()
  }, [fetchBillingData])

  const openPortalSession = useCallback(async (customerId) => {
    try {
      const url = apiEndpoints.billingPortalSession(customerId)
      const response = await authenticatedFetchJson(url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ return_url: window.location.href })
      })
      if (response?.data?.url) {
        window.open(response.data.url, '_blank', 'noopener,noreferrer')
      }
      return response?.data?.url
    } catch (err) {
      logger.error('Failed to create portal session:', err)
      throw err
    }
  }, [])

  const refetch = useCallback(() => {
    if (abortControllerRef.current) {
      abortControllerRef.current.abort()
    }
    const controller = new AbortController()
    abortControllerRef.current = controller
    return fetchBillingData(controller.signal)
  }, [fetchBillingData])

  return {
    customer,
    loading,
    error,
    refetch,
    openPortalSession
  }
}

export default useBillingData
